// utils/useRequest.ts
import { toast } from "@/components/common/Toast";
import { getCommonHeaders } from "./headers";
import Crypto from "@/utils/crypto";
import RSA from "@/utils/rsa";
import { convertObj, genKey } from "@/utils";
import Cookies from "js-cookie";
import { getDeviceId } from "./deviceId";
import { showGlobalLoading, hideGlobalLoading } from "./globalLoading";
import { getLang } from "./getLang";
import { getGlobalHost, getGlobalI18nLocale } from "./globalState";
import { isInApp } from "./bridge";
import { nativeMethod } from "./bridge/methods";

interface VestConfig {
  [key: string]: {
    testUrl: string;
    url: string;
  };
}
// 动态导入登录组件

// 创建一个全局事件总线来触发登录弹窗
export const loginEventBus = {
  listeners: new Set<() => void>(),
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  },
  emit() {
    this.listeners.forEach((listener) => listener());
  },
};

// 导出触发登录弹窗的函数
export const showLoginModal = () => {
  if (typeof window !== "undefined") {
    loginEventBus.emit();
  }
};

type LoginStrategy = "web" | "native" | "auto";

const triggerLogin = (strategy: LoginStrategy = "web") => {
  if (typeof window === "undefined") return;

  const shouldOpenNativeLogin =
    strategy === "native" || (strategy === "auto" && isInApp());

  if (shouldOpenNativeLogin) {
    void nativeMethod.openLogin().catch((error) => {
      console.error("open native login failed", error);
    });
    return;
  }

  showLoginModal();
};

type RequestConfig<T = any> = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  redactLog?: (value: string) => string; // Log copies only; never changes the request.
  headers?: Record<string, string>;
  params?: Record<string, any>; // 用于 GET 请求的查询参数
  transform?: (result: any) => T;
  timeout?: number; // 请求超时时间（毫秒）
  signal?: AbortSignal; // 添加 AbortSignal 支持
  baseUrl?: string;
  skipLoginModal?: boolean; // 是否跳过登录弹窗，仅用于控制登录弹窗显示，不会包含在请求中
  showToast?: boolean; // 用于需要忽略登录弹窗弹出，但需要toast提示的情况
  checkLogin?: boolean; // 是否检查登录状态
  loading?: boolean; // 新增 loading 参数
  encrypt?: boolean; // 是否加密响应数据，默认为 true
  loginStrategy?: LoginStrategy; // 登录态失效时的拉起方式，默认沿用 H5 登录弹窗
  ignoreLoginError?: boolean; // 是否忽略未登录错误的统一处理（如弹登录、toast）
};

type RouterInfoInterface = {
  token?: string;
  deviceid?: string;
  lang?: string;
};
// 定义响应数据结构
type ApiResponse<T = any> = {
  code: string;
  msg: string;
  data: T;
};
function getSign(params = {}, uuid?: string, timestamp?: number) {
  console.log("params====", params);
  // const queryString = `${timestamp}${convertObj(params, true)}`.replace(/[+]/g, '-').replace(/\//g, '_');
  const queryString =
    `${timestamp}${Crypto.base64Encode(convertObj(params, true))}`
      .replace(/[+]/g, "-")
      .replace(/\//g, "_");
  return Crypto.getMd5(Crypto.encrypt(queryString, uuid));
}

/** GET query 序列化：键值均经 encodeURIComponent，避免 + / 空格等与加签侧解析不一致（URLSearchParams 会把空格编成 +） */
function serializeGetParams(params: Record<string, unknown>): string {
  return Object.entries(params)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
    )
    .join("&");
}

const randomKey = genKey();

const useRequest = {
  async request<T>(
    url: string,
    data?: any,
    config: RequestConfig<T> = { method: "GET" },
    routeQuery?: any // 新增 query 参数
  ): Promise<T> {
    // 解构配置，分离出 checkLogin 和 skipLoginModal，这些参数不应该被包含在请求中
    const {
      redactLog = (value: string) => value,
      checkLogin, // 是否检查登录状态 仅用于更新local storage 不会包含在请求中
      skipLoginModal, // 是否跳过登录弹窗，仅用于控制登录弹窗显示，不会包含在请求中
      showToast, // 是否跳过登录弹窗，仅用于控制登录弹窗显示，不会包含在请求中
      loading, // 新增 loading 参数
      encrypt = true, // 是否加密响应数据，默认为 true
      loginStrategy = "web", // 登录态失效时的拉起方式，默认保持原有 H5 逻辑
      ignoreLoginError = false, // 是否忽略未登录错误的统一处理
      ...requestConfig // 其他请求配置
    } = config;

    const {
      method = "GET",
      headers = {},
      params,
      transform,
      signal,
      baseUrl: configBaseUrl,
      timeout: configTimeout,
    } = requestConfig;
    const isServer = typeof window === "undefined";
    // 使用 performance.now() 获取更精确的时间
    const startTime =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    try {
      // 获取当前 host
      const getCurrentHost = () => {
        if (isServer) {
          // 服务端使用全局存储的 host
          return getGlobalHost() || "test-h5.buscari.com";
        } else {
          // 客户端直接从 window.location 获取
          return window.location.host;
        }
      };
      const host = getCurrentHost();
      const vestListStr = process.env.NEXT_PUBLIC_VESTINFO || "[]";
      const cleanedValue = vestListStr.replace(/^['"]+|['"]+$/g, "").trim(); // 移除首尾的单引号或双引号
      const vestList: VestConfig[] = JSON.parse(cleanedValue);
      // 查找匹配的配置
      const matchedConfig = vestList.find((config) => {
        // 支持通配符匹配，例如 *.example.com
        const configHost = config.host.replace("*.", "");
        return host.includes(configHost);
      });

      const vestData = matchedConfig || {};
      const baseUrl =
        (process.env.NEXT_PUBLIC_ENV === "prod"
          ? vestData?.baseUrl
          : vestData?.testBaseUrl) || "https://test-h5-api.buscari.com";

      // const baseUrl = configBaseUrl || process.env.NEXT_PUBLIC_BASE_URL || "https://test-h5-api.buscari.com";
      // 获取通用 headers
      const commonHeaders = getCommonHeaders();
      // 构建 URL
      const fullUrl = params
        ? `${baseUrl}${url}?${serializeGetParams(params as Record<string, unknown>)}`
        : `${baseUrl}${url}`;

      const currentTime = new Date().getTime();
      let deviceId: string | undefined = undefined;
      const explicitDeviceId = headers.deviceId;
      const hasExplicitDeviceId =
        typeof explicitDeviceId === "string" &&
        explicitDeviceId.trim().length > 0 &&
        !["null", "undefined"].includes(explicitDeviceId.trim().toLowerCase());
      // 调用方已提供设备身份时，避免无用的指纹获取及其存储/初始化失败阻断请求。
      if (!hasExplicitDeviceId && typeof window !== "undefined") {
        deviceId = await getDeviceId();
      }
      const routeInfo: RouterInfoInterface = {};

      if (typeof window !== "undefined") {
        const searchParams = new URLSearchParams(window.location.search);
        const token = searchParams.get("token");
        const deviceid =
          searchParams.get("deviceId") || searchParams.get("deviceid");
        const lang =
          getGlobalI18nLocale() ||
          searchParams.get("lang") ||
          searchParams.get("language");
        Object.assign(routeInfo, {
          token,
          deviceid,
          lang,
        });
      }
      // DELETE 与 POST 加签规则对齐：
      // - 有请求体 data 时：与 POST 相同，用 data 加签并发 body
      // - 仅有 URL query（params）时：与 post(url, undefined, { params }) 相同，用 params 加签且不发 JSON body
      // - 无 data 且无 query 时：与 post(url, {}) 相同，用 {} 加签并发送 "{}"
      const hasQueryParams = Boolean(
        params &&
          typeof params === "object" &&
          Object.keys(params as Record<string, unknown>).length > 0
      );
      const signPayload =
        method === "GET"
          ? (params ?? {})
          : method === "DELETE" &&
              (data === undefined || data === null) &&
              !hasQueryParams
            ? {}
            : data || params;

      const customHeaders = {
        token: routeInfo.token || Cookies.get("token") || "",
        APP_AUTH_USER_ID: Cookies.get("uid") || "",
        lang: routeInfo.lang || getLang(routeQuery),
        // appLanguage: routeInfo.lang || getLang(routeQuery), // 用于网盘落地页 服务端判断语言 (后续去除了 延用lang)
        currentTime: currentTime.toString(),
        sign: getSign(signPayload, randomKey, currentTime),
        aesKey: RSA.RSAencrypt(randomKey),
        ...(routeInfo.deviceid || deviceId
          ? { deviceId: routeInfo.deviceid || deviceId }
          : {}),
      };

      // 合并 headers
      const requestHeaders = {
        ...commonHeaders,
        ...customHeaders,
        ...headers,
      };

      // 处理超时和取消
      let controller: AbortController | undefined;
      let finalSignal = signal;

      // 如果有超时设置但没有外部 signal，创建新的 AbortController
      if (configTimeout && !signal) {
        controller = new AbortController();
        finalSignal = controller.signal;
        setTimeout(() => controller?.abort(), configTimeout);
      }
      // 如果有超时设置且有外部 signal，使用 AbortSignal.any
      else if (configTimeout && signal) {
        controller = new AbortController();
        const timeoutSignal = controller.signal;
        setTimeout(() => controller?.abort(), configTimeout);

        // 使用 AbortSignal.any 组合两个 signal（如果浏览器支持）
        if (typeof AbortSignal.any === "function") {
          finalSignal = AbortSignal.any([signal, timeoutSignal]);
        } else {
          // 降级处理：使用外部 signal
          finalSignal = signal;
        }
      }

      // === 新增：loading 处理 ===
      if (loading) {
        if (typeof window !== "undefined") showGlobalLoading();
      }

      // console.log(`Request started: ${method} ${fullUrl}`, requestHeaders)
      // console.log('data: ', data)
      const requestBody =
        method === "GET"
          ? undefined
          : data !== undefined && data !== null
            ? JSON.stringify(data)
            : method === "DELETE" && !hasQueryParams
              ? JSON.stringify({})
              : undefined;

      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        body: requestBody,
        signal: finalSignal,
      });

      const endTime =
        typeof performance !== "undefined" ? performance.now() : Date.now();

      // 如果是 SSE 格式，返回原始 response 对象
      if (headers["Accept"] === "text/event-stream") {
        return response as unknown as T;
      }

      // 非 2xx 状态码或非 JSON 响应时，先拦截，避免 response.json() 解析 HTML 崩溃
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.includes("application/json")) {
        const bodyText = await response.text().catch(() => "");
        console.error("HTTP Error:", {
          url: redactLog(fullUrl),
          status: response.status,
          statusText: redactLog(response.statusText),
          contentType,
          body: redactLog(bodyText).slice(0, 200),
        });
        const error = new Error(
          `HTTP ${response.status}: ${response.statusText || "request failed"}`
        );
        Object.assign(error, { code: String(response.status), data: null });
        throw error;
      }

      const result: ApiResponse<T> = await response.json();
      if (result.code !== "00000") {
        console.error("API Error:", {
          code: result.code,
          message: redactLog(result.msg || ""),
          url: redactLog(fullUrl),
        });
        const error = new Error(result.msg || "API request failed");
        Object.assign(error, {
          code: result.code,
          data: result.data,
        });

        const isLoginError =
          result.code === "-40009" || result.code === "-10003";

        // 处理登录态失效错误码，根据登录策略分流到 H5 或宿主登录弹窗
        if (isLoginError) {
          if (checkLogin) {
            localStorage.setItem("isLoggedIn", "false");
          }
          if (!ignoreLoginError && !skipLoginModal) {
            triggerLogin(loginStrategy);
          }
          // 删除cookie和local storage token等信息
          Cookies.set("token", "");
          Cookies.set("uid", "");
        } else if (result.code === "00000" && checkLogin) {
          localStorage.setItem("isLoggedIn", "true");
        }

        if (
          (!skipLoginModal || showToast) &&
          !(isLoginError && ignoreLoginError)
        ) {
          console.log("result.msg", redactLog(result.msg || ""));
          // 非 -40009 错误，且未设置 skipLoginModal 时显示 toast
          toast.error(result.msg || "API request failed");
        }
        // throw error;
      }

      // 使用 performance.now() 计算持续时间
      const duration =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        startTime;

      const ecy = response.headers.get("ecy");
      // 检查响应头中的 ecy 字段，如果等于 1 则进行解密
      if (ecy === "1" && result.data) {
        const jsonStr = Crypto.decrypt(result.data, randomKey);
        result.data = JSON.parse(jsonStr);
      }

      // 直接返回 data 字段
      return transform ? transform(result) : result;
      // return transform ? transform(JSON.parse(jsonStr)) : JSON.parse(jsonStr);
    } catch (error: any) {
      // 如果是取消错误，不显示 toast
      if (error.name === "AbortError") {
        console.log("Request aborted:", redactLog(url));
        throw error;
      }

      // 根据 skipLoginModal 决定是否显示 toast
      if (!config.skipLoginModal || config.showToast) {
        toast.error(error.message);
      }
      console.error("Request error details:", {
        url: redactLog(url),
        method,
        error:
          error instanceof Error ? redactLog(error.message) : "Unknown error",
        stack:
          error instanceof Error && error.stack
            ? redactLog(error.stack)
            : undefined,
      });
      const duration =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        startTime;
      // console.log(JSON.stringify({
      //   url,
      //   method,
      //   duration: duration.toFixed(2),
      //   environment: typeof window === 'undefined' ? 'Server' : 'Client'
      // }))
      throw error;
    } finally {
      // === 新增：请求结束后隐藏 loading ===
      if (loading) {
        if (typeof window !== "undefined") hideGlobalLoading();
      }
    }
  },

  // GET 请求：params 作为 URL 参数
  get<T>(
    url: string,
    params?: any,
    config?: Omit<RequestConfig<T>, "method">,
    routeQuery?: any
  ) {
    console.log(
      "get request: ",
      config?.redactLog?.(url) ?? url,
      params,
      config
    );
    return this.request<T>(
      url,
      undefined,
      { ...config, method: "GET", params },
      routeQuery
    );
  },

  // POST 请求：data 作为请求体
  post<T>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig<T>, "method">,
    routeQuery?: any
  ) {
    return this.request<T>(
      url,
      data,
      { ...config, method: "POST" },
      routeQuery
    );
  },

  put<T>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig<T>, "method">,
    routeQuery?: any
  ) {
    return this.request<T>(url, data, { ...config, method: "PUT" }, routeQuery);
  },

  delete<T>(
    url: string,
    data?: any,
    config?: Omit<RequestConfig<T>, "method">,
    routeQuery?: any
  ) {
    return this.request<T>(
      url,
      data,
      { ...config, method: "DELETE" },
      routeQuery
    );
  },
};

export default useRequest;
